/**
 * Bannière globale affichée quand l'app est en mode dégradé
 */

import { AlertTriangle, X, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useDegradedMode } from '@/contexts/DegradedModeContext';

interface DegradedModeBannerProps {
  className?: string;
}

export function DegradedModeBanner({ className }: DegradedModeBannerProps) {
  const { 
    isInDegradedMode, 
    degradedFeatures, 
    recentErrors,
    exitDegradedMode,
    clearErrors 
  } = useDegradedMode();
  
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!isInDegradedMode || dismissed) {
    return null;
  }

  const unresolvedErrors = recentErrors.filter(e => !e.resolved).length;

  return (
    <Alert 
      variant="destructive"
      className={cn(
        'fixed top-0 left-0 right-0 z-50 rounded-none border-x-0 border-t-0',
        'bg-warning/10 border-warning/30 text-foreground',
        className
      )}
    >
      <div className="container">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-warning" />
            <div>
              <AlertTitle className="text-warning font-semibold">
                Mode dégradé actif
              </AlertTitle>
              <AlertDescription className="text-sm text-muted-foreground">
                Certaines fonctionnalités peuvent être limitées en raison de problèmes techniques.
              </AlertDescription>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {unresolvedErrors > 0 && (
              <Badge variant="secondary" className="bg-warning/20">
                {unresolvedErrors} erreur(s)
              </Badge>
            )}
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="gap-1"
            >
              Détails
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                clearErrors();
                exitDegradedMode();
              }}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Réessayer
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDismissed(true)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Détails expandables */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-warning/20 space-y-3">
            {/* Fonctionnalités désactivées */}
            {degradedFeatures.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Fonctionnalités affectées:</h4>
                <div className="flex flex-wrap gap-2">
                  {degradedFeatures.map(f => (
                    <Badge key={f.featureId} variant="outline" className="text-xs">
                      {f.featureId}
                      {f.fallbackAvailable && ' (fallback actif)'}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Erreurs récentes */}
            {unresolvedErrors > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">Erreurs récentes:</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {recentErrors
                    .filter(e => !e.resolved)
                    .slice(0, 5)
                    .map(e => (
                      <div key={e.id} className="text-xs text-muted-foreground flex items-center gap-2">
                        <span className="font-mono">[{e.error.category}]</span>
                        <span>{e.error.userMessage}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Alert>
  );
}
